import { useQuery } from '@tanstack/react-query';

import { useAccessToken } from '@hooks/useAccessToken';
import {
  PlannerApiError,
  fetchPlannerState,
  isPlannerNotFound,
  type PlannerStateResponse
} from '@api-service/planner';

const FIVE_MINUTES_IN_MS = 5 * 60 * 1000;
const MAX_RETRY_ATTEMPTS = 2;

export type UsePlannerStateOptions = {
  enabled?: boolean;
  staleTime?: number;
};

export type UsePlannerStateResult = {
  data: PlannerStateResponse | null;
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
  error: unknown;
  hasTeam: boolean;
  refetch: () => void;
};

const isClientError = (status: number) => status >= 400 && status < 500;

/**
 * Fetches the current user's planner state from ffh-backend.
 * Returns `data: null` and `hasTeam: false` when the backend reports 404
 * (no planner yet) so the caller can render an onboarding prompt.
 */
export const usePlannerState = (
  options: UsePlannerStateOptions = {}
): UsePlannerStateResult => {
  const { enabled = true, staleTime = FIVE_MINUTES_IN_MS } = options;
  const { accessToken, accessTokenIsFetching } = useAccessToken();

  const query = useQuery<PlannerStateResponse | null>({
    queryKey: ['plannerState'],
    queryFn: async () => {
      if (!accessToken) {
        throw new Error('No access token available');
      }

      try {
        return await fetchPlannerState({ accessToken });
      } catch (caught) {
        if (isPlannerNotFound(caught)) {
          return null;
        }
        throw caught;
      }
    },
    enabled: enabled && Boolean(accessToken) && !accessTokenIsFetching,
    staleTime,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    refetchOnReconnect: false,
    retry: (failureCount, caught) => {
      if (caught instanceof PlannerApiError && isClientError(caught.status)) {
        return false;
      }
      return failureCount < MAX_RETRY_ATTEMPTS;
    }
  });

  return {
    data: query.data ?? null,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isError: query.isError,
    error: query.error,
    hasTeam: query.data != null,
    refetch: () => {
      query.refetch();
    }
  };
};
